class ThistleMobileMenu extends HTMLElement {
  constructor() {
    super();
    this.stack = [];
    this.lastFocused = null;
    this.boundKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.openButton = this.querySelector('[data-mobile-menu-open]');
    this.drawer = this.querySelector('[data-mobile-menu-drawer]');
    this.panels = new Map(
      Array.from(this.querySelectorAll('[data-menu-panel]')).map((panel) => [
        panel.dataset.menuPanel,
        panel,
      ])
    );

    this.addEventListener('click', (event) => {
      const open = event.target.closest('[data-mobile-menu-open]');
      if (open) {
        event.preventDefault();
        this.open(open);
        return;
      }

      const close = event.target.closest('[data-mobile-menu-close]');
      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      const target = event.target.closest('[data-menu-target]');
      if (target) {
        event.preventDefault();
        this.forward(target.dataset.menuTarget, target);
        return;
      }

      const back = event.target.closest('[data-menu-back]');
      if (back) {
        event.preventDefault();
        this.back();
      }
    });

    this.drawer?.addEventListener('keydown', this.boundKeydown);
    this.reset();
  }

  reset() {
    this.stack = [{ id: 'root', opener: this.openButton }];
    this.renderPanels();
  }

  closeCollectionSheets() {
    document.querySelectorAll('.mobile-facets__wrapper > details[open]').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll('[data-mobile-sort-sheet].is-open').forEach((sheet) => {
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
    });

    document.documentElement.classList.remove('collection-mobile-sheet-open');
  }

  open(opener) {
    if (!this.drawer) return;

    this.closeCollectionSheets();

    this.lastFocused = opener || document.activeElement;
    this.reset();

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.openButton?.setAttribute('aria-expanded', 'true');

    document.body.classList.add('tt-mobile-menu-open', 'overflow-hidden-tablet');
    document.documentElement.classList.add('tt-mobile-menu-open');

    requestAnimationFrame(() => {
      const closeButton = this.panels.get('root')?.querySelector('[data-mobile-menu-close]');
      closeButton?.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.openButton?.setAttribute('aria-expanded', 'false');

    document.body.classList.remove('tt-mobile-menu-open', 'overflow-hidden-tablet');
    document.documentElement.classList.remove('tt-mobile-menu-open');

    this.reset();

    const focusTarget = this.lastFocused || this.openButton;
    requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
  }

  forward(panelId, opener) {
    if (!panelId || !this.panels.has(panelId)) return;

    const predictiveSearch = this.panels.get('root')?.querySelector('thistle-predictive-search');
    if (typeof predictiveSearch?.close === 'function') predictiveSearch.close();

    this.stack.push({ id: panelId, opener });
    this.renderPanels();

    requestAnimationFrame(() => {
      this.panels.get(panelId)?.querySelector('[data-menu-back]')?.focus({ preventScroll: true });
    });
  }

  back() {
    if (this.stack.length <= 1) {
      this.close();
      return;
    }

    const leaving = this.stack.pop();
    const current = this.stack[this.stack.length - 1];

    this.renderPanels();

    requestAnimationFrame(() => {
      if (leaving?.opener) {
        leaving.opener.focus({ preventScroll: true });
        return;
      }

      this.panels
        .get(current.id)
        ?.querySelector('[data-menu-back]')
        ?.focus({ preventScroll: true });
    });
  }

  renderPanels() {
    const activeId = this.stack[this.stack.length - 1]?.id || 'root';
    const stackIds = this.stack.map((entry) => entry.id);

    this.panels.forEach((panel, id) => {
      const isActive = id === activeId;
      const isBehind = !isActive && stackIds.includes(id);

      panel.classList.toggle('is-active', isActive);
      panel.classList.toggle('is-behind', isBehind);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (isActive) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    });
  }

  onKeydown(event) {
    if (!this.drawer?.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.stack.length > 1 ? this.back() : this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const activePanel = this.panels.get(this.stack[this.stack.length - 1]?.id || 'root');
    if (!activePanel) return;

    const focusable = Array.from(
      activePanel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('inert') && element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('tt-mobile-menu')) {
  customElements.define('tt-mobile-menu', ThistleMobileMenu);
}
